import { useState, useCallback } from 'react'
import { useWallet } from '@solana/wallet-adapter-react'
import { sendClaimTransaction, parseClaimError, ClaimPayload } from '../services/claim.service'
import { api } from '../services/api'

interface UseClaimRewardResult {
  loading: boolean
  error: string | null
  txid: string | null
  claimInvestmentUsdt: (investmentId: string) => Promise<string>
  claimInvestmentTakara: (investmentId: string) => Promise<string>
  claimReferralUsdt: () => Promise<string>
  claimReferralTakara: () => Promise<string>
}

export function useClaimReward(): UseClaimRewardResult {
  const { publicKey, sendTransaction } = useWallet()
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState<string | null>(null)
  const [txid, setTxid]       = useState<string | null>(null)

  const executeClaim = useCallback(
    async (payload: ClaimPayload): Promise<string> => {
      if (!publicKey || !sendTransaction) {
        throw new Error('Wallet not connected')
      }

      setLoading(true)
      setError(null)
      setTxid(null)

      try {
        const txSignature = await sendClaimTransaction(payload, publicKey, sendTransaction)
        setTxid(txSignature)

        // Notify backend and check confirmation status
        if (payload.claimLogId) {
          const confirmRes = await api.confirmClaim(payload.claimLogId, txSignature)
          const status = confirmRes?.data?.status
          if (status === 'NONCE_MISMATCH') {
            throw new Error('NONCE_MISMATCH')
          }
          if (status === 'TX_FAILED') {
            throw new Error('TX_FAILED')
          }
          // PENDING_CONFIRMATION — expiry job will verify automatically, treat as success
        }

        return txSignature
      } catch (err: any) {
        // If the tx was sent but confirmTransaction timed out, err.txSignature is set.
        // Still notify the backend so it can verify on-chain and auto-confirm via the expiry job.
        if (err.txSignature && payload.claimLogId) {
          setTxid(err.txSignature)
          api.confirmClaim(payload.claimLogId, err.txSignature).catch(() => {
            // Fire-and-forget — expiry job will auto-confirm if nonce advanced
          })
        }

        const msg = parseClaimError(err)
        setError(msg)
        throw new Error(msg)
      } finally {
        setLoading(false)
      }
    },
    [publicKey, sendTransaction],
  )

  /** Parse error only if it hasn't been parsed already (by executeClaim) */
  const handleError = (err: any): never => {
    // executeClaim already calls parseClaimError and throws new Error(msg).
    // If err.message is already a user-friendly string, don't re-parse it
    // (re-parsing would lose context and return "Something went wrong").
    const hasRawData = err?.logs || err?.response || err?._enriched
    let msg: string

    if (hasRawData) {
      // Raw error with data — parse it
      msg = parseClaimError(err)
    } else if (err instanceof Error && err.message && !looksRaw(err.message)) {
      // Already parsed by executeClaim — pass through
      msg = err.message
    } else {
      // Unknown or raw-looking message — parse as fallback
      msg = parseClaimError(err)
    }

    setError(msg)
    setLoading(false)
    throw new Error(msg)
  }

  /**
   * Detect raw/technical error messages that should NOT be shown to users.
   * If it looks raw, we re-parse or replace with a generic message.
   */
  function looksRaw(message: string): boolean {
    const rawPatterns = [
      /^[A-Z][a-z]+Error:/,       // TypeError:, ReferenceError:, etc.
      /0x[0-9a-f]{3,}/i,          // hex codes like 0x177d
      /\bError processing\b/i,    // Solana RPC errors
      /\bInstruction \d+\b/,      // "Instruction 4: custom program error"
      /\b(simulation|simulate)\b/i,// "Transaction simulation failed"
      /\{.*"Custom".*\}/,         // JSON error payloads {"Custom":6013}
      /\bprogram error\b/i,       // "custom program error"
      /\bundefined\b/,            // literal "undefined"
      /\bnull\b/,                 // literal "null"
      /failed with status/i,      // "Request failed with status code 429"
      /ECONNREFUSED|ETIMEDOUT/,   // Node network errors
      /\b[A-Z_]{5,}\b/,          // SCREAMING_CASE like NONCE_MISMATCH
    ]
    return rawPatterns.some(p => p.test(message))
  }

  const claimInvestmentUsdt = useCallback(
    async (investmentId: string): Promise<string> => {
      setLoading(true)
      setError(null)
      try {
        const res = await api.claimYield(investmentId)
        const payload = res.data as ClaimPayload
        return await executeClaim(payload)
      } catch (err: any) {
        handleError(err)
      }
    },
    [executeClaim],
  )

  const claimInvestmentTakara = useCallback(
    async (investmentId: string): Promise<string> => {
      setLoading(true)
      setError(null)
      try {
        const res = await api.claimTakara(investmentId)
        const payload = res.data as ClaimPayload
        return await executeClaim(payload)
      } catch (err: any) {
        handleError(err)
      }
    },
    [executeClaim],
  )

  const claimReferralUsdt = useCallback(async (): Promise<string> => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.claimReferralUsdt()
      const payload = res.data as ClaimPayload
      return await executeClaim(payload)
    } catch (err: any) {
      handleError(err)
    }
  }, [executeClaim])

  const claimReferralTakara = useCallback(async (): Promise<string> => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.claimReferralTakara()
      const payload = res.data as ClaimPayload
      return await executeClaim(payload)
    } catch (err: any) {
      handleError(err)
    }
  }, [executeClaim])

  return {
    loading,
    error,
    txid,
    claimInvestmentUsdt,
    claimInvestmentTakara,
    claimReferralUsdt,
    claimReferralTakara,
  }
}
